import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Building2, Mail, Phone, Lock, User, Loader2 } from "lucide-react"
import { Logo } from "src/components/Logo"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { useAuth } from "src/contexts/AuthContext"
import { fetchInvitationByUuid, acceptInvitation } from "src/features/members/api/invitationsApi"

// Indian mobile: 10 digits starting 6-9 (matches the rest of the app). The
// backend stores E.164, so we prepend +91 on submit.
const INDIAN_PHONE_RE = /^[6-9]\d{9}$/

const acceptSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  phone: z.string()
    .transform((v) => v.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, ""))
    .refine((v) => INDIAN_PHONE_RE.test(v), "Enter a valid 10-digit Indian mobile number"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ["password_confirmation"],
})

export default function AcceptInvitationPage() {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const { setCurrentUser, setOrganization, setOrganizations } = useAuth()

  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(acceptSchema),
    mode: "onBlur",
  })

  useEffect(() => {
    async function loadInvitation() {
      try {
        const { invitation: data } = await fetchInvitationByUuid(uuid)
        setInvitation(data)
      } catch (err) {
        setError(err.response?.data?.error || "Invalid or expired invitation")
      } finally {
        setLoading(false)
      }
    }

    if (uuid) loadInvitation()
  }, [uuid])

  async function onSubmit(values) {
    setSubmitting(true)
    setError(null)

    try {
      // The backend stores phones in E.164; the schema has already normalised
      // `phone` to a bare 10-digit Indian number, so prepend the +91 prefix.
      const payload = { ...values, phone: `+91${values.phone}` }
      const response = await acceptInvitation(uuid, payload)

      // Update auth context
      setCurrentUser(response.user)
      setOrganization(response.organization)
      setOrganizations(response.organizations || [])

      // Update CSRF token
      const meta = document.querySelector('meta[name="csrf-token"]')
      if (meta && response.csrf_token) {
        meta.setAttribute("content", response.csrf_token)
      }

      // Redirect to dashboard
      navigate("/dashboard", { replace: true })
    } catch (err) {
      const serverErrors = err.response?.data?.errors || ["Failed to accept invitation"]
      setError(serverErrors[0])
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8fafe]">
        <Loader2 className="h-8 w-8 animate-spin text-[#4b5bed]" />
      </div>
    )
  }

  if (error && !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8fafe] p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100">
            <Mail className="h-7 w-7 text-rose-600" />
          </div>
          <h1 className="mb-2 text-xl font-bold text-slate-900">Invalid Invitation</h1>
          <p className="mb-6 text-sm text-slate-600">{error}</p>
          <Button onClick={() => navigate("/")} variant="outline">
            Go to Homepage
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8fafe] p-4">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <Logo />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
          {/* Invitation Details */}
          <div className="mb-6 rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-blue-900">
                  You've been invited to join
                </p>
                <p className="mt-1 text-base font-bold text-blue-900">
                  {invitation.organization_name}
                </p>
                <p className="mt-1 text-xs text-blue-700">
                  as <span className="font-semibold">{invitation.role_name}</span>
                </p>
              </div>
            </div>
          </div>

          <h1 className="mb-2 text-2xl font-bold text-slate-900">Complete Your Profile</h1>
          <p className="mb-6 text-sm text-slate-600">
            Fill in your details to accept the invitation and join the team.
          </p>

          {error && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email (read-only) */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={invitation.email}
                  disabled
                  className="pl-10 bg-slate-50"
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-sm font-medium">
                Full Name
              </Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  className="pl-10"
                  {...register("name")}
                  disabled={submitting}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-xs text-rose-600">{errors.name.message}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-sm font-medium">
                Phone Number
              </Label>
              <div className="relative mt-1.5">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="98765 43210"
                  maxLength={10}
                  className="pl-10"
                  {...register("phone")}
                  disabled={submitting}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-xs text-rose-600">{errors.phone.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10"
                  {...register("password")}
                  disabled={submitting}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-rose-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="password_confirmation" className="text-sm font-medium">
                Confirm Password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password_confirmation"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10"
                  {...register("password_confirmation")}
                  disabled={submitting}
                />
              </div>
              {errors.password_confirmation && (
                <p className="mt-1 text-xs text-rose-600">
                  {errors.password_confirmation.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting Invitation...
                </>
              ) : (
                "Accept Invitation & Join"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
